import {useRef, useEffect} from "react"
import { getChartColors, getChartColorsWithAlpha } from './chartColors';

export const mapVacancyData = (vacanciesData) => {
  return vacanciesData?.vacancies.map(vacancy => {
    // Try to get company ID from multiple possible locations
    const companyId = vacancy.campaign?.company?.id || 
                      vacancy.company_id || 
                      vacancy.campaign?.company_id ||
                      vacancy.campaign_id;
    
    const companyName = vacancy.campaign?.company?.name || 
                        vacancy.company_name ||
                        vacancy.campaign?.company_name;
    
    return {
      id: vacancy.id,
      title: vacancy.title,
      slug: vacancy.slug,
      company_id: companyId, // Store company_id at root level for easy access
      campaign: {
        title: vacancy.campaign?.title || 'No Campaign',
        id: vacancy.campaign?.id,
        company_id: companyId, // Also store in campaign for backward compatibility
        company: {
          id: companyId,
          name: companyName
        }
      },
      status: vacancy.status,
      applicants: vacancy.vacancy_applications_count || 0,
      newApplicants: vacancy.vacancy_new_applications_count || 0,
      tags: vacancy.vacancy_tag?.map(tag => tag.title) || [],
      salary: parseFloat(vacancy.salary) || 0,
      startDate: vacancy.start_date,
      endDate: vacancy.end_date,
      recruiters: vacancy.campaign?.company?.company_users?.map(user => user.user.name) || []
    };
  });
};

export const isLightColor = (color) => {
  // Convert hex to RGB
  const hex = color.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6;
};

export const formattedDate = (datetime, with_time=true, with_seconds=false) => {
  let _new_date = new Date(datetime).toLocaleDateString();
  if(with_time){
    const _time_format = {hour: '2-digit', minute:'2-digit'};
    if(with_seconds){
      _time_format.second = '2-digit';
    }
    let _time = new Date(datetime).toLocaleTimeString([],_time_format)
    _new_date += " " + _time;
  } 
  return _new_date;
};

export const useDebounce = (callback, delay=1000) => {
  const timeOutRef = useRef(null);

  function debouncedFunction(...args){
    clearTimeout(timeOutRef.current);
    timeOutRef.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }

  useEffect(() => {
    return () => clearTimeout(timeOutRef.current);
  }, []);
  return debouncedFunction;
}
//add a function  to  increase the auto  height of text area 
export const autoResizeTextarea = (textarea, maxHeight = 500) => {
  textarea.style.height = 'auto';
  const newHeight = Math.min(textarea.scrollHeight, maxHeight);
  textarea.style.height = `${newHeight}px`;
  textarea.style.overflowY = newHeight >= maxHeight ? 'auto' : 'hidden';
};


export const generateSeriesColors = (requiredNumberOfColors, baseColor=null) => {
  // Use the new chart color palette
  return getChartColors(requiredNumberOfColors, baseColor);
}

export const generateSeriesColorsAlpha = (requiredNumberOfColors, baseColor=null) => {
  // Use the new chart color palette with alpha
  return getChartColorsWithAlpha(requiredNumberOfColors, baseColor, 0.8);
}

/**
 * Phone number utilities
 */

/**
 * Validates if a phone number is valid
 * @param {string} phoneNumber - The phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false;
  
  // Basic validation - should start with + and contain only digits, spaces, and dashes
  const phoneRegex = /^\+[1-9]\d{1,14}$/;
  return phoneRegex.test(phoneNumber.replace(/[\s\-\(\)]/g, ''));
};

/**
 * Formats a phone number for display
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-digit characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');
  
  // If it doesn't start with +, add it
  if (!cleaned.startsWith('+')) {
    return `+${cleaned}`;
  }
  
  return cleaned;
};

/**
 * Extracts country code from phone number
 * @param {string} phoneNumber - The phone number
 * @returns {string} - Country code (e.g., "+44", "+1")
 */
export const extractCountryCode = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  const match = phoneNumber.match(/^\+(\d+)/);
  return match ? `+${match[1]}` : '';
};

/**
 * Gets the default country based on user's location or preference
 * @returns {string} - Country code (e.g., "GB", "US")
 */
export const getDefaultCountry = () => {
  // You can implement logic here to detect user's country
  // For now, default to GB (United Kingdom)
  return 'GB';
};

/**
 * Generates a URL-friendly slug from a title
 * @param {string} title - The title to convert to slug
 * @returns {string} - URL-friendly slug
 */
export const generateSlug = (title) => {
  if (!title) return '';
  
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading and trailing hyphens
};

export const truncateToWords = (str, numWords = 118) => {
  // Split the string into an array of words using space as a delimiter
  return str.split(' ').slice(0, numWords).join(' ');
};

/**
 * Rewrites storage URLs to use the API server origin (e.g. local backend vs production APP_URL).
 */
export const resolveStorageUrl = (url, apiUrl = import.meta.env.VITE_API_URL) => {
  if (!url) return null;

  try {
    const apiOrigin = new URL(apiUrl).origin;
    const { pathname } = new URL(url);
    return `${apiOrigin}${pathname}`;
  } catch {
    return url;
  }
};

